import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ShareIcon from "@mui/icons-material/Share";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
    Alert,
    Box,
    Chip,
    Container,
    Divider,
    FormControl,
    Grid2 as Grid,
    IconButton,
    InputLabel,
    LinearProgress,
    Link,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListSubheader,
    MenuItem,
    Paper,
    Popover,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import CopyToClipboard from "../components/CopyToClipboard";
import { resolveApiBaseURL } from "../config/apiHost";
import { useRoleLabels } from "../hooks/useRoleLabels";
import type { RoleLabelsByKey } from "../object-actions/types/types";
import AdminIconWrapper from "../theme/icons/AdminIcon";
import AthletesIconWrapper from "../theme/icons/AthletesIcon";
import CoachesIconWrapper from "../theme/icons/CoachesIcon";
import ParentsIconWrapper from "../theme/icons/ParentsIcon";
import { applyRoleLabels } from "../utils/roleLabels";

const POLL_MS = 30_000;

const AUDIT_ROLE_FILTER_ALL = "all";

type CheckStatus = "ok" | "error" | "warning" | "unknown" | "degraded";


interface HealthCheckItem {
    label: string;
    status: CheckStatus;
    detail: string;
    last_run_at?: string;
    age_seconds?: number;
}

interface HealthPayload {
    timestamp?: string;
    overall: CheckStatus;
    checks: HealthCheckItem[]
}


const SIMULATOR_MEDIA_BASE = "https://storage.googleapis.com/strongmsp-media/media/simulator";

interface QaSimulatorRun {
    /** Scenario name or `full_program`; matches `media/simulator/<id>/`. */
    id: string;
    label: string;
    description: string;
    screenshots: string;
    videoUrl: string;
    stepsJsonUrl: string;
    srtUrl: string;
    posterUrl: string;
}

/** Published simulator recordings under `media/simulator/<id>/mobile/`. Add entries when a run completes. */
const QA_RUNS: QaSimulatorRun[] = [
    {
        id: "full_program",
        label: "Full program",
        description: "End-to-end linear program flow across all roles and stages.",
        screenshots: `${SIMULATOR_MEDIA_BASE}/full_program/mobile/screenshots`,
        videoUrl: `${SIMULATOR_MEDIA_BASE}/full_program/mobile/demo.webm`,
        stepsJsonUrl: `${SIMULATOR_MEDIA_BASE}/full_program/mobile/demo_captions.json`,
        srtUrl: `${SIMULATOR_MEDIA_BASE}/full_program/mobile/demo_captions.srt`,
        posterUrl: `${SIMULATOR_MEDIA_BASE}/full_program/mobile/demo.png`,
    },
    {
        id: "self_pay_coach_invites_athlete_guardian",
        label: "Self-pay coach invites athlete & guardian",
        description:
            "Coach registers, buys Starter, claims coach, signs waiver, invites guardian; guardian and athlete accept and complete onboarding.",
        screenshots: `${SIMULATOR_MEDIA_BASE}/self_pay_coach_invites_athlete_guardian/mobile/screenshots`,
        videoUrl: `${SIMULATOR_MEDIA_BASE}/self_pay_coach_invites_athlete_guardian/mobile/demo.webm`,
        stepsJsonUrl: `${SIMULATOR_MEDIA_BASE}/self_pay_coach_invites_athlete_guardian/mobile/demo_captions.json`,
        srtUrl: `${SIMULATOR_MEDIA_BASE}/self_pay_coach_invites_athlete_guardian/mobile/demo_captions.srt`,
        posterUrl: `${SIMULATOR_MEDIA_BASE}/self_pay_coach_invites_athlete_guardian/mobile/demo.png`,
    },
];

const DEFAULT_QA_RUN_ID = QA_RUNS[0]?.id ?? "full_program";

interface AuditVideoStepSegment {
    title: string;
    /** Flow section from simulator JSON (e.g. Pre-Assessment). */
    stage?: string;
    /** Simulator persona for this step (e.g. coach, athlete). */
    role?: string;
    start_sec: number;
    end_sec: number | null;
}

const MIN_AUDIT_CUE_SEC = 0.05;

/** Default English title for a simulator machine role before party-role substitution. */
function defaultAuditRoleTitle(role: string): string {
    const r = role.trim();
    return r.charAt(0).toUpperCase() + r.slice(1).toLowerCase();
}

function auditRoleDisplayLabel(
    role: string | undefined,
    labels?: RoleLabelsByKey | null
): string | null {
    const r = role?.trim();
    if (!r) {
        return null;
    }
    return applyRoleLabels(defaultAuditRoleTitle(r), labels);
}

function AuditRoleIcon({
    role,
    sx,
}: {
    role?: string;
    sx?: React.ComponentProps<typeof AthletesIconWrapper>["sx"];
}) {
    const iconSx = { fontSize: "1.15rem", color: "text.secondary", ...sx };
    switch (role?.trim().toLowerCase()) {
        case "athlete":
            return <AthletesIconWrapper sx={iconSx} />;
        case "coach":
            return <CoachesIconWrapper sx={iconSx} />;
        case "parent":
        case "guardian":
            return <ParentsIconWrapper sx={iconSx} />;
        case "admin":
        case "agent":
            return <AdminIconWrapper sx={iconSx} />;
        default:
            return <HelpOutlineIcon sx={iconSx} />;
    }
}

function formatSegmentTitleWithRole(
    role: string | undefined,
    title: string,
    labels?: RoleLabelsByKey | null
): string {
    const label = auditRoleDisplayLabel(role, labels);
    if (!label) {
        return title;
    }
    return `${label} · ${title}`;
}

function vttEscape(text: string): string {
    return text.replace(/&/g, "&amp;").replace(/</g, "&lt;");
}

/** Match `simulator_log_to_captions` timestamp formatting. */
function vttTimestamp(seconds: number): string {
    let s = Number.isFinite(seconds) && seconds >= 0 ? seconds : 0;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    s = s % 60;
    let whole = Math.floor(s);
    let ms = Math.round((s - whole) * 1000);
    if (ms >= 1000) {
        whole += 1;
        ms = 0;
    }
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(whole).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
}

function buildWebVttFromSegments(
    segments: AuditVideoStepSegment[],
    labels?: RoleLabelsByKey | null
): string {
    const lines = ["WEBVTT", ""];
    let idx = 0;
    for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];
        const next = segments[i + 1];
        const start = seg.start_sec;
        let end = seg.end_sec ?? next?.start_sec ?? start + MIN_AUDIT_CUE_SEC;
        if (end - start < MIN_AUDIT_CUE_SEC) {
            end = start + MIN_AUDIT_CUE_SEC;
        }
        const text = formatSegmentTitleWithRole(seg.role, seg.title, labels);
        idx += 1;
        lines.push(String(idx));
        lines.push(`${vttTimestamp(start)} --> ${vttTimestamp(end)}`);
        lines.push(vttEscape(text));
        lines.push("");
    }
    return `${lines.join("\n")}\n`;
}

/** Shareable /qa link with run, deep-link timestamp (`t`), and readable step label (`step`). */
function buildAuditStepShareUrl(startSec: number, stepLabel: string, runId: string): string {
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set("run", runId);
    url.searchParams.set("t", String(startSec));
    url.searchParams.set("step", stepLabel);
    return url.toString();
}

function formatAuditTimestamp(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds < 0) {
        return "0:00";
    }
    const total = Math.floor(seconds);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) {
        return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }
    return `${m}:${String(s).padStart(2, "0")}`;
}

function parseRoleLabelsFromJson(data: unknown): RoleLabelsByKey | null {
    if (!data || typeof data !== "object") {
        return null;
    }
    const raw = (data as { role_labels?: unknown }).role_labels;
    if (!raw || typeof raw !== "object") {
        return null;
    }
    const labels = raw as Record<string, unknown>;
    const athlete = typeof labels.athlete === "string" ? labels.athlete.trim() : "";
    const coach = typeof labels.coach === "string" ? labels.coach.trim() : "";
    const parent = typeof labels.parent === "string" ? labels.parent.trim() : "";
    if (!athlete && !coach && !parent) {
        return null;
    }
    return {
        athlete: athlete || "Athlete",
        coach: coach || "Coach",
        parent: parent || "Guardian",
    };
}

function parseAuditSegmentsFromJson(data: unknown): AuditVideoStepSegment[] {
    if (!data || typeof data !== "object") {
        return [];
    }
    const steps = (data as { steps?: unknown }).steps;
    if (!Array.isArray(steps)) {
        return [];
    }
    const out: AuditVideoStepSegment[] = [];
    for (const raw of steps) {
        if (!raw || typeof raw !== "object") {
            continue;
        }
        const step = raw as {
            title?: unknown;
            stage?: unknown;
            role?: unknown;
            start_sec?: unknown;
            end_sec?: unknown;
        };
        const title = typeof step.title === "string" ? step.title.trim() : "";
        if (!title) {
            continue;
        }
        const stageRaw = step.stage;
        const stage =
            typeof stageRaw === "string" && stageRaw.trim()
                ? stageRaw.trim()
                : undefined;
        const roleRaw = step.role;
        const role =
            typeof roleRaw === "string" && roleRaw.trim() ? roleRaw.trim() : undefined;
        const start = Number(step.start_sec);
        if (!Number.isFinite(start)) {
            continue;
        }
        const endRaw = step.end_sec;
        const end = endRaw === undefined || endRaw === null ? null : Number(endRaw);
        out.push({
            title,
            stage,
            role,
            start_sec: start,
            end_sec: end !== null && Number.isFinite(end) ? end : null,
        });
    }
    out.sort((a, b) => a.start_sec - b.start_sec);
    return out;
}

/** Preserves stage order by first appearance in the (time-sorted) step list. */
/** Distinct machine roles (case-insensitive merge) for filter options. */
function distinctAuditRoles(segments: AuditVideoStepSegment[]): string[] {
    const seen = new Map<string, string>();
    for (const seg of segments) {
        const r = seg.role?.trim();
        if (!r) {
            continue;
        }
        const key = r.toLowerCase();
        if (!seen.has(key)) {
            seen.set(key, r);
        }
    }
    return Array.from(seen.values()).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "base" })
    );
}

function groupAuditSegmentsByStage(
    segments: AuditVideoStepSegment[]
): { stageLabel: string; segments: AuditVideoStepSegment[] }[] {
    const order: string[] = [];
    const byStage = new Map<string, AuditVideoStepSegment[]>();
    for (const seg of segments) {
        const label = seg.stage?.trim() || "Uncategorized";
        if (!byStage.has(label)) {
            byStage.set(label, []);
            order.push(label);
        }
        byStage.get(label)!.push(seg);
    }
    return order.map((stageLabel) => ({
        stageLabel,
        segments: byStage.get(stageLabel)!,
    }));
}

type DisplayStatus = "Operational" | "Degraded" | "Unavailable" | "Unknown";

function toDisplayStatus(s: CheckStatus): DisplayStatus {
    if (s === "ok") {
        return "Operational";
    }
    if (s === "error") {
        return "Unavailable";
    }
    if (s === "warning" || s === "degraded") {
        return "Degraded";
    }
    return "Unknown";
}

function statusChipColor(
    s: CheckStatus
): "success" | "error" | "warning" | "default" {
    if (s === "ok") {
        return "success";
    }
    if (s === "error") {
        return "error";
    }
    if (s === "warning" || s === "degraded") {
        return "warning";
    }
    return "default";
}

function statusChipIcon(s: CheckStatus) {
    if (s === "ok") {
        return <CheckCircleIcon />;
    }
    if (s === "error") {
        return <ErrorOutlineIcon />;
    }
    if (s === "warning" || s === "degraded") {
        return <WarningAmberIcon />;
    }
    return <HelpOutlineIcon />;
}


const QualityAssurance: React.FC = () => {
    const { labels: orgRoleLabels } = useRoleLabels();
    const [searchParams, setSearchParams] = useSearchParams();
    const auditVideoRef = useRef<HTMLVideoElement | null>(null);
    const auditTimelineScrollRef = useRef<HTMLDivElement | null>(null);
    const auditStageHeaderRefs = useRef<Map<string, HTMLElement>>(new Map());
    const [health, setHealth] = useState<HealthPayload | null>(null);
    const [healthLoading, setHealthLoading] = useState(true);
    const [healthError, setHealthError] = useState<string | null>(null);

    const initialRunId = useMemo(() => {
        const fromUrl = searchParams.get("run")?.trim();
        if (fromUrl && QA_RUNS.some((run) => run.id === fromUrl)) {
            return fromUrl;
        }
        return DEFAULT_QA_RUN_ID;
    }, [searchParams]);

    const [selectedRunId, setSelectedRunId] = useState<string>(initialRunId);
    const selectedRun = useMemo(
        () => QA_RUNS.find((run) => run.id === selectedRunId) ?? QA_RUNS[0],
        [selectedRunId]
    );

    const [auditSegments, setAuditSegments] = useState<AuditVideoStepSegment[]>([]);
    const [auditRoleLabelsFromJson, setAuditRoleLabelsFromJson] = useState<RoleLabelsByKey | null>(null);
    const [auditSegmentsLoading, setAuditSegmentsLoading] = useState(true);
    const [auditSegmentsError, setAuditSegmentsError] = useState<string | null>(null);
    const [auditVttBlobUrl, setAuditVttBlobUrl] = useState<string | null>(null);
    const [auditRoleFilter, setAuditRoleFilter] = useState<string>(AUDIT_ROLE_FILTER_ALL);
    const [auditStagePopoverAnchor, setAuditStagePopoverAnchor] = useState<HTMLElement | null>(null);

    const auditRoleLabels = auditRoleLabelsFromJson ?? orgRoleLabels;

    const resolveAuditRoleLabel = useCallback(
        (role: string | undefined) => auditRoleDisplayLabel(role, auditRoleLabels),
        [auditRoleLabels]
    );

    const auditRoleOptions = useMemo(
        () => distinctAuditRoles(auditSegments),
        [auditSegments]
    );

    useEffect(() => {
        if (auditRoleFilter === AUDIT_ROLE_FILTER_ALL) {
            return;
        }
        const valid = auditRoleOptions.some((r) => r.toLowerCase() === auditRoleFilter);
        if (!valid) {
            setAuditRoleFilter(AUDIT_ROLE_FILTER_ALL);
        }
    }, [auditRoleFilter, auditRoleOptions]);

    const seekAuditVideo = useCallback((seconds: number) => {
        const el = auditVideoRef.current;
        if (!el) {
            return;
        }
        let t = Math.max(0, seconds);
        if (Number.isFinite(el.duration) && el.duration > 0) {
            t = Math.min(t, el.duration);
        }
        el.currentTime = t;
        void el.play().catch(() => {
            console.error("Failed to play audit video");
        });
    }, []);

    const validAuditUrlSec = useMemo((): number | null => {
        const raw = searchParams.get("t");
        if (raw === null || raw === "") {
            return null;
        }
        const sec = Number.parseFloat(raw);
        return Number.isFinite(sec) ? sec : null;
    }, [searchParams]);

    /**
     * Browsers allow muted autoplay without a gesture; unmute after playback actually starts.
     * `autoPlay` starts playback — it does not read `?t=`, so we only set `currentTime` here + in the effect below.
     */
    const [auditDeepLinkAudioUnblocked, setAuditDeepLinkAudioUnblocked] = useState(false);
    useEffect(() => {
        setAuditDeepLinkAudioUnblocked(false);
    }, [validAuditUrlSec, selectedRunId]);

    const auditVideoMuted =
        validAuditUrlSec !== null && !auditDeepLinkAudioUnblocked;

    const applyAuditDeepLinkSeek = useCallback(() => {
        if (validAuditUrlSec === null) {
            return;
        }
        const el = auditVideoRef.current;
        if (!el) {
            return;
        }
        let t = Math.max(0, validAuditUrlSec);
        if (Number.isFinite(el.duration) && el.duration > 0) {
            t = Math.min(t, el.duration);
        }
        el.currentTime = t;
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    }, [validAuditUrlSec]);

    /** Client-side `?t=` change while metadata is already available (no second `loadedmetadata`). */
    useEffect(() => {
        if (validAuditUrlSec === null) {
            return;
        }
        const el = auditVideoRef.current;
        if (!el || el.readyState < HTMLMediaElement.HAVE_METADATA) {
            return;
        }
        applyAuditDeepLinkSeek();
    }, [validAuditUrlSec, applyAuditDeepLinkSeek]);

    const fetchHealth = useCallback(async () => {
        const base = resolveApiBaseURL().replace(/\/$/, "");
        const url = `${base}/api/health/`;
        try {
            const res = await fetch(url, { credentials: "omit" });
            if (!res.ok) {
                setHealthError("Service status could not be loaded.");
                setHealth(null);
                return;
            }
            const data = (await res.json()) as HealthPayload;
            setHealth(data);
            setHealthError(null);
        } catch {
            setHealthError("Service status could not be loaded.");
            setHealth(null);
        } finally {
            setHealthLoading(false);
        }
    }, []);

    useEffect(() => {
        void fetchHealth();
        const id = window.setInterval(() => void fetchHealth(), POLL_MS);
        return () => window.clearInterval(id);
    }, [fetchHealth]);

    useEffect(() => {
        const fromUrl = searchParams.get("run")?.trim();
        if (fromUrl && QA_RUNS.some((run) => run.id === fromUrl) && fromUrl !== selectedRunId) {
            setSelectedRunId(fromUrl);
        }
    }, [searchParams, selectedRunId]);

    const handleSelectQaRun = useCallback(
        (runId: string) => {
            setSelectedRunId(runId);
            setAuditRoleFilter(AUDIT_ROLE_FILTER_ALL);
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set("run", runId);
                    next.delete("t");
                    next.delete("step");
                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    useEffect(() => {
        let cancelled = false;
        setAuditSegmentsLoading(true);
        setAuditSegmentsError(null);
        setAuditSegments([]);
        setAuditRoleLabelsFromJson(null);
        void (async () => {
            try {
                const res = await fetch(selectedRun.stepsJsonUrl, {
                    mode: "cors",
                    credentials: "omit",
                });
                if (!res.ok) {
                    throw new Error(`Could not load audit steps (${res.status})`);
                }
                const data = (await res.json()) as unknown;
                const segments = parseAuditSegmentsFromJson(data);
                const roleLabels = parseRoleLabelsFromJson(data);
                if (!cancelled) {
                    setAuditSegments(segments);
                    setAuditRoleLabelsFromJson(roleLabels);
                }
            } catch {
                if (!cancelled) {
                    setAuditSegments([]);
                    setAuditSegmentsError("Could not load audit step list.");
                }
            } finally {
                if (!cancelled) {
                    setAuditSegmentsLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [selectedRun.stepsJsonUrl]);

    useEffect(() => {
        if (!auditSegments.length) {
            setAuditVttBlobUrl(null);
            return;
        }
        const body = buildWebVttFromSegments(auditSegments, auditRoleLabels);
        const blob = new Blob([body], { type: "text/vtt" });
        const url = URL.createObjectURL(blob);
        setAuditVttBlobUrl(url);
        return () => {
            URL.revokeObjectURL(url);
        };
    }, [auditSegments, auditRoleLabels]);

    const serviceRows = useMemo(() => health?.checks ? health.checks : [], [health]);

    const filteredAuditSegments = useMemo(() => {
        if (auditRoleFilter === AUDIT_ROLE_FILTER_ALL) {
            return auditSegments;
        }
        return auditSegments.filter(
            (s) => (s.role?.trim().toLowerCase() ?? "") === auditRoleFilter
        );
    }, [auditSegments, auditRoleFilter]);

    const auditSegmentsByStage = useMemo(
        () => groupAuditSegmentsByStage(filteredAuditSegments),
        [filteredAuditSegments]
    );

    const scrollToAuditStage = useCallback((stageLabel: string) => {
        const el = auditStageHeaderRefs.current.get(stageLabel);
        const container = auditTimelineScrollRef.current;
        if (!el || !container) {
            return;
        }
        const containerTop = container.getBoundingClientRect().top;
        const elTop = el.getBoundingClientRect().top;
        container.scrollTo({
            top: elTop - containerTop + container.scrollTop,
            behavior: "smooth",
        });
    }, []);

    const auditStagePopoverOpen = Boolean(auditStagePopoverAnchor);

    const openAuditStagePopover = (event: React.MouseEvent<HTMLElement>) => {
        setAuditStagePopoverAnchor(event.currentTarget);
    };

    const closeAuditStagePopover = () => {
        setAuditStagePopoverAnchor(null);
    };

    const handlePickAuditStage = (stageLabel: string) => {
        scrollToAuditStage(stageLabel);
        closeAuditStagePopover();
    };

    return (
        <Container maxWidth="lg" sx={{ py: 8 }}>
            <Box sx={{ mb: 6, textAlign: "center" }}>
                <Typography variant="h3" component="h1" gutterBottom fontWeight="700">
                    Quality Assurance & System Integrity
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: "auto" }}>
                    At Strong Mind Strong Performance, our commitment to human development is matched by our
                    dedication to technical excellence and user safety.
                </Typography>
                {health?.timestamp ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Last checked: {new Date(health.timestamp).toLocaleString()} · refreshes every{" "}
                        {POLL_MS / 1000}s
                    </Typography>
                ) : null}
            </Box>

            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                Real-Time System Health
            </Typography>

            {healthLoading && !health && <LinearProgress sx={{ mb: 2 }} />}
            {healthError ? (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    {healthError}
                </Alert>
            ) : null}

            <TableContainer component={Paper} elevation={2} sx={{ mb: 6, borderRadius: 2 }}>
                <Table>
                    <TableHead sx={{ bgcolor: "action.hover" }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>Service Layer</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {health && serviceRows.length > 0
                            ? serviceRows.map((row: HealthCheckItem) => (
                                <TableRow key={row.label}>
                                    <TableCell sx={{ fontWeight: 500 }}>{row.label}</TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={statusChipIcon(row.status)}
                                            label={toDisplayStatus(row.status)}
                                            color={statusChipColor(row.status)}
                                            variant="outlined"
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary" display="block">
                                            {row.status}
                                        </Typography>
                                        {row.detail ? (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                display="block"
                                                sx={{ mt: 0.5 }}
                                            >
                                                {row.detail}
                                            </Typography>
                                        ) : null}
                                    </TableCell>
                                </TableRow>
                            ))
                            : null}
                        {!health && !healthLoading ? (
                            <TableRow>
                                <TableCell colSpan={3}>
                                    <Typography variant="body2" color="text.secondary">
                                        No health data yet.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : null}
                        {healthLoading && !health ? (
                            <TableRow>
                                <TableCell colSpan={3}>
                                    <Typography variant="body2" color="text.secondary">
                                        Loading system health…
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : null}
                    </TableBody>
                </Table>
            </TableContainer>

            <Divider sx={{ my: 6 }} />

            <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    End-to-End Reliability Testing
                </Typography>
                <Typography variant="body1" paragraph color="text.secondary">
                    Our E2E Testing Suite mimics real-world user behavior across every possible role. Tests verify permissions security, ARIA compliance, and overall functionality.
                    Select a completed simulator run below to review its integrity audit recording.
                </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
                <FormControl fullWidth size="small">
                    <InputLabel id="qa-run-select-label">Simulation run</InputLabel>
                    <Select
                        labelId="qa-run-select-label"
                        id="qa-run-select"
                        label="Simulation run"
                        fullWidth
                        value={selectedRunId}
                        onChange={(e) => handleSelectQaRun(e.target.value)}
                    >
                        {QA_RUNS.map((run) => (
                            <MenuItem key={run.id} value={run.id}>
                                {run.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {selectedRun?.description ? (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 1, textAlign: "center" }}
                    >
                        {selectedRun.description}
                    </Typography>
                ) : null}
            </Box>

            <Grid container spacing={1} sx={{ justifyContent: "center", alignItems: "flex-start", textAlign: "center" }}
            >
                <Grid sx={{ minWidth: '375px' }} >
                    <video
                        key={selectedRunId}
                        ref={auditVideoRef}
                        src={selectedRun.videoUrl}
                        crossOrigin="anonymous"
                        controls
                        poster={selectedRun.posterUrl}
                        playsInline
                        muted={auditVideoMuted}
                        onLoadedMetadata={applyAuditDeepLinkSeek}
                        onPlaying={() => setAuditDeepLinkAudioUnblocked(true)}
                        preload={validAuditUrlSec !== null ? "auto" : "metadata"}
                        autoPlay={validAuditUrlSec !== null}
                        style={{
                            width: "100%",
                            maxWidth: "375px",
                            height: "100%",
                            display: "block",
                            objectFit: "contain"
                        }}
                    >
                        {auditVttBlobUrl ? (
                            <track
                                key={auditVttBlobUrl}
                                kind="captions"
                                srcLang="en"
                                label="English"
                                src={auditVttBlobUrl}
                                default
                            />
                        ) : null}
                    </video>
                </Grid>
                <Grid
                    sx={{
                        height: "667px",
                        maxHeight: "667px",
                        minWidth: "375px",
                        display: "flex",
                        flexDirection: "column",
                        px: 1,
                        minHeight: 0,
                        textAlign: "left",
                    }}
                >
                    <Box sx={{ flexShrink: 0 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ textAlign: "center" }}>
                            Audit Timeline
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
                            <FormControl fullWidth size="small" sx={{ textAlign: "left" }}>
                                <InputLabel id="audit-timeline-role-filter-label">Role</InputLabel>
                                <Select
                                    labelId="audit-timeline-role-filter-label"
                                    id="audit-timeline-role-filter"
                                    label="Role"
                                    value={auditRoleFilter}
                                    onChange={(e) => setAuditRoleFilter(e.target.value)}
                                >
                                    <MenuItem value={AUDIT_ROLE_FILTER_ALL}>All</MenuItem>
                                    {auditRoleOptions.map((role) => {
                                        const value = role.toLowerCase();
                                        const label = resolveAuditRoleLabel(role) ?? role;
                                        return (
                                            <MenuItem key={value} value={value}>
                                                {label}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                            <Tooltip title="Scroll to stage">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={openAuditStagePopover}
                                        disabled={auditSegmentsLoading || auditSegmentsByStage.length === 0}
                                        aria-label="Scroll to stage"
                                        aria-haspopup="dialog"
                                        aria-expanded={auditStagePopoverOpen}
                                    >
                                        <ListAltIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Popover
                                open={auditStagePopoverOpen}
                                anchorEl={auditStagePopoverAnchor}
                                onClose={closeAuditStagePopover}
                                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                transformOrigin={{ vertical: "top", horizontal: "right" }}
                                slotProps={{
                                    paper: {
                                        sx: { minWidth: 220, maxWidth: 320, maxHeight: 360 },
                                    },
                                }}
                            >
                                <List dense disablePadding aria-label="Audit stages">
                                    <ListSubheader component="div" sx={{ lineHeight: 2 }}>
                                        Scroll to stage
                                    </ListSubheader>
                                    {auditSegmentsByStage.map((group) => (
                                        <ListItemButton
                                            key={group.stageLabel}
                                            onClick={() => handlePickAuditStage(group.stageLabel)}
                                        >
                                            <ListItemText
                                                primary={group.stageLabel}
                                                primaryTypographyProps={{ noWrap: true }}
                                            />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </Popover>
                        </Box>
                        {auditSegmentsError ? (
                            <Alert severity="warning" sx={{ mb: 2, flexShrink: 0 }}>
                                {auditSegmentsError}
                            </Alert>
                        ) : null}
                        {auditSegmentsLoading ? (
                            <LinearProgress sx={{ mb: 2, flexShrink: 0 }} />
                        ) : null}
                    </Box>
                    <Box
                        ref={auditTimelineScrollRef}
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflowY: "auto",
                            WebkitOverflowScrolling: "touch",
                        }}
                    >
                        <List dense disablePadding>
                            {auditSegmentsByStage.map((group, groupIndex) => {
                                const isLastGroup = groupIndex === auditSegmentsByStage.length - 1;
                                return (
                                    <React.Fragment key={group.stageLabel}>
                                        <ListSubheader
                                            disableSticky
                                            component="div"
                                            ref={(node) => {
                                                if (node) {
                                                    auditStageHeaderRefs.current.set(group.stageLabel, node);
                                                } else {
                                                    auditStageHeaderRefs.current.delete(group.stageLabel);
                                                }
                                            }}
                                            sx={{
                                                py: 1,
                                                px: 0,
                                                mt: groupIndex > 0 ? 2.5 : 0.5,
                                                mb: 0.5,
                                                lineHeight: 1.3,
                                                bgcolor: "transparent",
                                            }}
                                        >
                                            <Typography
                                                variant="overline"
                                                color="text.secondary"
                                                fontWeight={700}
                                                letterSpacing={1.1}
                                                display="block"
                                            >
                                                {group.stageLabel}
                                            </Typography>
                                            <Divider sx={{ mt: 0.75, opacity: 0.6 }} />
                                        </ListSubheader>
                                        {group.segments.map((seg, indexInGroup) => {
                                            const timeLabel =
                                                seg.end_sec !== null
                                                    ? `${formatAuditTimestamp(seg.start_sec)} – ${formatAuditTimestamp(seg.end_sec)}`
                                                    : formatAuditTimestamp(seg.start_sec);
                                            const roleLabel = resolveAuditRoleLabel(seg.role);
                                            const shareLabel = formatSegmentTitleWithRole(
                                                seg.role,
                                                seg.title,
                                                auditRoleLabels
                                            );
                                            const isLastInGroup = indexInGroup === group.segments.length - 1;
                                            const showDivider = !(isLastGroup && isLastInGroup);
                                            return (
                                                <ListItem
                                                    key={`${group.stageLabel}-${seg.start_sec}-${indexInGroup}`}
                                                    disablePadding
                                                    sx={{ flexDirection: "column", alignItems: "stretch" }}
                                                    secondaryAction={
                                                        <Tooltip title="Copy link to this timestamp">
                                                            <span>
                                                                <CopyToClipboard
                                                                    textToCopy={buildAuditStepShareUrl(
                                                                        Math.floor(seg.start_sec),
                                                                        shareLabel,
                                                                        selectedRunId
                                                                    )}
                                                                    copiedMessage="Link copied"
                                                                >
                                                                    <ShareIcon style={{ width: '16px', height: '16px' }}
                                                                        fontSize="small" />
                                                                </CopyToClipboard>
                                                            </span>
                                                        </Tooltip>
                                                    }
                                                >
                                                    <ListItemButton
                                                        onClick={() => seekAuditVideo(seg.start_sec)}
                                                        aria-label={`Jump video to ${timeLabel}: ${shareLabel}`}
                                                        sx={{ alignItems: "flex-start", py: 1.25, pr: 6, width: "100%" }}
                                                    >
                                                        <Grid
                                                            container
                                                            width="100%"
                                                            alignItems="center"
                                                            spacing={1.25}
                                                        >
                                                            <Grid
                                                                size={{ xs: 4, md: 3 }}
                                                                sx={{
                                                                    borderRight: 1,
                                                                    borderColor: "divider",
                                                                    pr: 1,
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        alignItems: "flex-start",
                                                                        gap: 0.75,
                                                                    }}
                                                                >
                                                                    <AuditRoleIcon role={seg.role} />
                                                                    <Box sx={{ minWidth: 0 }}>
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                fontSize: "0.775rem",
                                                                                color: "text.secondary",
                                                                                lineHeight: 1.2,
                                                                            }}
                                                                        >
                                                                            {roleLabel ?? ""}
                                                                        </Typography>
                                                                        <Typography
                                                                            variant="caption"
                                                                            color="text.disabled"
                                                                            sx={{
                                                                                fontSize: "0.65rem",
                                                                                lineHeight: 1.1,
                                                                                display: "block",
                                                                            }}
                                                                        >
                                                                            {timeLabel}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Grid>
                                                            <Grid size={{ xs: 8, md: 9 }}>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        color: "primary.main",
                                                                        lineHeight: 1.35,
                                                                    }}
                                                                >
                                                                    {seg.title}
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </ListItemButton>
                                                    {showDivider ? (
                                                        <Divider sx={{ borderColor: "divider" }} />
                                                    ) : null}
                                                </ListItem>
                                            );
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </List>
                        {!auditSegmentsLoading && !auditSegmentsError && auditSegments.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                No simulator steps loaded.
                            </Typography>
                        ) : null}
                        {!auditSegmentsLoading &&
                            !auditSegmentsError &&
                            auditSegments.length > 0 &&
                            filteredAuditSegments.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                No steps for this role.
                            </Typography>
                        ) : null}
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2, flexShrink: 0 }}>
                            Captions: in-player (WebVTT). Also available as{" "}
                            <Link href={selectedRun.srtUrl} download target="_blank" rel="noopener noreferrer">
                                SRT
                            </Link>
                            .
                        </Typography>
                    </Box>
                </Grid>
            </Grid>

            {/* <Alert severity="info" sx={{ mt: 6, borderRadius: 2 }}>
                <strong>Safety Protocol:</strong> Our AI content monitoring is a non-negotiable part of our
                stack. If latency exceeds 200ms, our system is designed to fail-safe, ensuring human
                intervention is prioritized.
            </Alert> */}
        </Container >
    );
};

export default QualityAssurance;
